/**
 * Browser Command Executor
 *
 * Owns the `PlaywrightClient` lifecycle and the command dispatch table that
 * turns a wire-level `{ method, args }` pair into a call against
 * `IBrowserClient`. Extracted from `LocalBrowserHost` so it can be reused by
 * transports other than the CLI's dial-out WebSocket client — e.g. the
 * microVM guest server, which is a WebSocket *server* the worker dials into.
 *
 * @module browser-command-executor
 */

import { mkdtemp, writeFile } from 'node:fs/promises';
import { tmpdir } from 'node:os';
import { join as pathJoin, dirname as pathDirname, basename as pathBasename } from 'node:path';
import {
  PlaywrightClient,
  type BrowserClientOptions,
  type BrowserConnectOptions,
  type BrowserLogger,
  type IBrowserClient,
  type BrowserTabSelector,
  type ArtifactUploadTarget,
} from '@chatsdet/browser-core';
import { mergeTraceArchives } from '@chatsdet/trace-tools';
import type { LocalBrowserMode } from './protocol.js';
import { putFileToPresignedUrl } from './artifact-upload.js';

/**
 * Turn a wire-side `connect` payload's inline `storageState` back into a local
 * file, because `PlaywrightClient.connect` only accepts a *path*.
 *
 * The peer sent JSON rather than a path on purpose: a `storageStatePath` names
 * a file on the worker, and this executor runs on the other machine (a microVM
 * guest, the customer-VPC host, the user's laptop). See
 * `RemoteBrowserClient.connect`, which does the inlining.
 *
 * Materializing here rather than teaching `PlaywrightClient` about inline state
 * keeps the wire concern at the wire boundary — this function is the only place
 * that knows the payload arrived over a socket.
 */
async function materializeConnectStorageState(
  raw: unknown
): Promise<string | BrowserConnectOptions | undefined> {
  // The legacy string form and the absent form carry no inline state.
  if (raw === undefined || typeof raw === 'string') return raw;
  if (typeof raw !== 'object' || raw === null) return undefined;

  // A narrowed assertion, not `any`: this is the wire boundary, so the shape is
  // asserted exactly once here rather than being re-asserted at every use.
  const options = raw as BrowserConnectOptions;
  if (options.storageState === undefined) return options;

  const dir = await mkdtemp(pathJoin(tmpdir(), 'remote-connect-storage-state-'));
  const path = pathJoin(dir, 'storage-state.json');
  await writeFile(path, JSON.stringify(options.storageState), 'utf-8');

  // Hand PlaywrightClient the path and drop the inline copy so there is exactly
  // one source for the context's storage state.
  const { storageState: _inline, ...rest } = options;
  return { ...rest, storageStatePath: path };
}

/**
 * Materialize `FileUploadOptions.remoteFetchFiles` into local paths.
 *
 * `paths[i]` is a worker-local path — meaningless on this machine (a microVM
 * guest, the customer-VPC host, the user's laptop) — whenever
 * `remoteFetchFiles[i]` is present. Downloads that entry's presigned URL and
 * writes it to a fresh local tmp file under its ORIGINAL filename (so
 * Playwright reports the same `File.name` to the page the worker staged),
 * replacing the corresponding entry in the returned array. Entries without a
 * `remoteFetchFiles` counterpart pass through unchanged — same reasoning as
 * `materializeConnectStorageState`: this is the one place that knows the
 * payload arrived over a socket, so `client.fileUpload` never has to.
 */
async function materializeRemoteFetchFiles(
  paths: string[],
  remoteFetchFiles: Array<{ url: string; filename: string } | undefined> | undefined
): Promise<string[]> {
  if (!remoteFetchFiles || remoteFetchFiles.every((f) => !f)) return paths;

  return Promise.all(
    paths.map(async (originalPath, i) => {
      const remote = remoteFetchFiles[i];
      if (!remote) return originalPath;

      const response = await fetch(remote.url);
      if (!response.ok) {
        throw new Error(
          `[fileUpload] remote fetch of "${remote.filename}" failed: ${response.status} ${response.statusText}`
        );
      }
      // Own tmp dir per file (not a shared dir behind a check-then-set) — the
      // map callbacks below run concurrently, so a shared "create dir once"
      // guard would race: several callbacks could see no dir yet and each
      // call `mkdtemp`, with only the last assignment winning while earlier
      // callbacks' paths point at directories no longer referenced anywhere
      // else. Per-file dirs also sidestep same-filename collisions when an
      // upload step references two attachments sharing a filename.
      const dir = await mkdtemp(pathJoin(tmpdir(), 'remote-fetch-upload-'));
      // basename() strips any directory components (including `..` segments)
      // from the attachment's original, user-supplied filename — this is the
      // only thing standing between an untrusted filename and a path-traversal
      // write outside the freshly minted tmp dir.
      const safeFilename = pathBasename(remote.filename) || 'attachment';
      const localPath = pathJoin(dir, safeFilename);
      await writeFile(localPath, Buffer.from(await response.arrayBuffer()));
      return localPath;
    })
  );
}

export interface BrowserCommandExecutorOptions {
  browserMode: LocalBrowserMode;
  cdpUrl?: string;
  headless?: boolean;
  storageStatePath?: string;
  /** Inject an existing PlaywrightClient instead of creating a new one */
  existingClient?: PlaywrightClient;
  /** When true, skip browser launch in start() — used when browser is already managed externally */
  skipBrowserLaunch?: boolean;
  /**
   * Apply a later wire-level connect payload by swapping the context on the
   * browser launched by start(). MicroVM guests need this because readiness
   * requires Chromium to launch before the worker has assembled its final
   * storage-state and recording options.
   */
  reconfigureOnConnect?: boolean;
  logger?: (level: 'info' | 'warn' | 'error' | 'debug', message: string, data?: unknown) => void;
}

/**
 * BrowserCommandExecutor manages a `PlaywrightClient` instance and dispatches
 * wire-protocol commands (method name + positional args) to it. Transport
 * concerns (WebSocket client/server, HTTP, etc.) live entirely outside this
 * class.
 */
export class BrowserCommandExecutor {
  private options: BrowserCommandExecutorOptions;
  private playwrightClient: PlaywrightClient;

  constructor(options: BrowserCommandExecutorOptions) {
    this.options = options;
    if (options.existingClient) {
      this.playwrightClient = options.existingClient;
    } else {
      const logger: BrowserLogger = {
        debug: (msg: string, data?: Record<string, unknown>) => this.log('debug', msg, data),
        info: (msg: string, data?: Record<string, unknown>) => this.log('info', msg, data),
        warn: (msg: string, data?: Record<string, unknown>) => this.log('warn', msg, data),
        error: (msg: string, data?: Record<string, unknown>) => this.log('error', msg, data),
      };
      this.playwrightClient = new PlaywrightClient({ logger });
    }
  }

  private log(level: 'info' | 'warn' | 'error' | 'debug', message: string, data?: unknown): void {
    if (this.options.logger) {
      this.options.logger(level, message, data);
    }
  }

  get client(): PlaywrightClient {
    return this.playwrightClient;
  }

  // =========================================================================
  // Lifecycle
  // =========================================================================

  async start(): Promise<void> {
    if (this.options.skipBrowserLaunch) {
      return;
    }

    const { browserMode, cdpUrl, headless = true, storageStatePath } = this.options;
    await this.playwrightClient.connect({
      browserMode: headless ? 'headless' : 'headed',
      cdpUrl: browserMode === 'cdp' ? cdpUrl : undefined,
      storageStatePath,
    });
  }

  async stop(): Promise<void> {
    // Only disconnect the client if we own it (not injected externally)
    if (!this.options.existingClient) {
      await this.playwrightClient.disconnect();
    }
  }

  // =========================================================================
  // Method Execution — Delegate to PlaywrightClient
  // =========================================================================

  /**
   * Maps incoming command method names to PlaywrightClient methods.
   * The client implements IBrowserClient, so all standard browser operations
   * are available and behave identically to the cloud environment.
   *
   * ## Why the `as any` casts below
   *
   * This is the wire boundary. `args` arrives as `unknown[]` — it was JSON off a
   * socket a moment ago, so at this point it genuinely carries no type
   * information, and `method` is an arbitrary string. The casts are not papering
   * over a type error in our own code; they are the unavoidable re-assertion of
   * a shape that only the peer's `RemoteBrowserClient` call site knows.
   *
   * They are deliberately NOT tightened here, for two reasons:
   *
   *  1. Casting to the real option types would be *worse than useless* — it
   *     would read as validation while performing none. An unvalidated
   *     `args[0] as ClickOptions` is exactly as unsafe as `as any` but invites
   *     the reader to believe otherwise.
   *  2. Real safety at this boundary means parsing, not asserting — a per-method
   *     schema that rejects malformed payloads before dispatch. That is a
   *     worthwhile change and a large one (~55 methods), and it belongs in its
   *     own PR with its own tests, not smuggled into a move.
   *
   * Until then the honest invariant is: **this method trusts its peer.** Both
   * peers are our own code (`RemoteBrowserClient` → this executor), the
   * transport is authenticated, and a malformed payload surfaces as a thrown
   * error from PlaywrightClient rather than silent corruption. The
   * dispatch-parity test (`executor.dispatch-parity.test.ts`) guards the one
   * drift this shape actually permits — a method existing on one side and not
   * the other.
   */
  async execute(method: string, args: unknown[]): Promise<unknown> {
    const client = this.playwrightClient as IBrowserClient;

    switch (method) {
      // Lifecycle
      case 'connect': {
        const connectArg = await materializeConnectStorageState(args[0]);
        if (
          this.options.reconfigureOnConnect &&
          typeof connectArg === 'object' &&
          connectArg !== null
        ) {
          if (!client.swapContext) {
            throw new Error('Browser cannot apply connect options to its pre-launched context');
          }
          await client.swapContext({
            storageStatePath: connectArg.storageStatePath,
            storageStateRequired: connectArg.storageStateRequired,
            recordVideo: connectArg.recordVideo,
            extraHTTPHeaders: connectArg.extraHTTPHeaders,
            httpCredentials: connectArg.httpCredentials,
          });
          if (connectArg.recordVideo) {
            // A pre-launched remote browser must be recording-ready before connect
            // is acknowledged. This intentionally allows saved video to include
            // authentication; BrowserSession's deferred boundary controls workflow
            // action/timeline capture, not this remote encoder's first frame.
            client.ensureScreencastEncoder?.();
          }
          return;
        }
        return client.connect(connectArg);
      }
      case 'disconnect':
        return client.disconnect();

      // Navigation
      case 'navigate':
        return client.navigate(args[0] as string, args[1] as any);
      case 'navigateBack':
        return client.navigateBack(args[0] as any);

      // Page Inspection
      case 'snapshot':
        return client.snapshot(args[0] as any);
      case 'takeScreenshot':
        return client.takeScreenshot(args[0] as any);
      case 'evaluate':
        return client.evaluate(args[0] as string, args[1] as any);
      case 'runCode':
        return client.runCode(args[0] as string, args[1] as any);
      case 'consoleMessages':
        return client.consoleMessages(args[0] as any);
      case 'networkRequests':
        return client.networkRequests(args[0] as any);

      // Interaction
      case 'click':
        return client.click(args[0] as string, args[1] as string, args[2] as any);
      case 'clickAtCoordinates':
        return client.clickAtCoordinates(
          args[0] as number,
          args[1] as number,
          args[2] as string,
          args[3] as any
        );
      case 'moveToCoordinates':
        return client.moveToCoordinates(
          args[0] as number,
          args[1] as number,
          args[2] as string,
          args[3] as any
        );
      case 'dragCoordinates':
        return client.dragCoordinates(
          args[0] as number,
          args[1] as number,
          args[2] as number,
          args[3] as number,
          args[4] as string,
          args[5] as any
        );
      case 'hover':
        return client.hover(args[0] as string, args[1] as string, args[2] as any);
      case 'drag':
        return client.drag(
          args[0] as string,
          args[1] as string,
          args[2] as string,
          args[3] as string,
          args[4] as any
        );
      case 'type':
        return client.type(
          args[0] as string,
          args[1] as string,
          args[2] as string,
          args[3] as boolean,
          args[4] as any
        );
      case 'pressKey':
        return client.pressKey(args[0] as string, args[1] as any);
      case 'fillForm':
        return client.fillForm(args[0] as any[], args[1] as any);
      case 'selectOption':
        return client.selectOption(
          args[0] as string,
          args[1] as string,
          args[2] as string,
          args[3] as any
        );
      case 'fileUpload': {
        const uploadOpts = args[1] as
          | { remoteFetchFiles?: Array<{ url: string; filename: string } | undefined> }
          | undefined;
        const materializedPaths = await materializeRemoteFetchFiles(
          args[0] as string[],
          uploadOpts?.remoteFetchFiles
        );
        return client.fileUpload(materializedPaths, args[1] as any);
      }

      // Scroll
      case 'scroll':
        return client.scroll(
          args[0] as any,
          args[1] as any,
          args[2] as any,
          args[3] as any,
          args[4] as any
        );

      // Dialogs
      case 'handleDialog':
        return client.handleDialog(
          args[0] as 'accept' | 'dismiss',
          args[1] as string,
          args[2] as any
        );

      // Waiting
      case 'waitFor':
        return client.waitFor(args[0] as any);

      // Browser Management
      case 'close':
        return client.close(args[0] as any);
      case 'resize':
        return client.resize(args[0] as number, args[1] as number, args[2] as any);
      case 'tabs':
        return client.tabs(
          args[0] as 'list' | 'new' | 'close' | 'select',
          args[1] as BrowserTabSelector,
          args[2] as any
        );

      // Context Management
      case 'swapContext':
        return client.swapContext?.(args[0] as any);

      // Storage & Page Info
      case 'getStorageState':
        return client.getStorageState(args[0] as any);
      case 'getAuthDiagnostics':
        return client.getAuthDiagnostics?.() ?? null;
      case 'getCurrentUrl':
        return client.getCurrentUrl(args[0] as any);
      case 'getTitle':
        return client.getTitle(args[0] as any);
      case 'getLinks':
        return client.getLinks(args[0] as any);
      case 'getElementBoundingBox':
        return client.getElementBoundingBox(args[0] as string, args[1] as any);

      // PDF
      case 'fetchPdfText':
        return client.fetchPdfText(args[0] as string, args[1] as BrowserClientOptions | undefined);
      case 'fetchPdfImage':
        return client.fetchPdfImage(args[0] as string, args[1] as BrowserClientOptions | undefined);

      // Tracing
      case 'startTracing':
        return client.startTracing?.(args[0] as any);
      case 'stopTracing':
        return this.execStopTracing(
          args[0] as (BrowserClientOptions & { upload?: ArtifactUploadTarget }) | undefined
        );

      // Video
      case 'isVideoRecordingEnabled':
        return client.isVideoRecordingEnabled?.() ?? false;
      case 'saveVideo':
        return this.execSaveVideo(args[0] as { upload?: ArtifactUploadTarget } | undefined);
      case 'getVideoPath':
        return client.getVideoPath?.() ?? null;

      // Screencast
      case 'startScreencast':
        return client.startScreencast?.(args[0] as any, args[1] as any);
      case 'stopScreencast':
        return client.stopScreencast?.();
      case 'isScreencastActive':
        return client.isScreencastActive?.() ?? false;

      default:
        throw new Error(`Unknown method: ${method}`);
    }
  }

  // =========================================================================
  // Artifact egress — stopTracing/saveVideo with an `upload` target
  // =========================================================================
  //
  // When `opts.upload` is present, this process (the microVM guest, when this
  // executor is embedded there) is expected to merge/finalize the artifact
  // and PUT it directly to S3 rather than handing a local path back to the
  // caller — the caller (the worker, over the WS wire) cannot read this
  // process's filesystem. Both methods fall through to the plain
  // `IBrowserClient` call, unmodified, when `upload` is absent — e.g. every
  // non-microVM caller of this same executor (the CLI's local browser).

  private async execStopTracing(
    opts: (BrowserClientOptions & { upload?: ArtifactUploadTarget }) | undefined
  ): Promise<Awaited<ReturnType<IBrowserClient['stopTracing']>>> {
    const client = this.playwrightClient as IBrowserClient;
    const result = await client.stopTracing(opts);
    if (!opts?.upload) return result;

    // A session with a re-auth context swap produces more than one trace
    // chunk. The worker cannot merge them (it has no local copies), so the
    // guest merges in-process before uploading a single archive.
    const chunkPaths = result.traces && result.traces.length > 0 ? result.traces : [result.trace];
    const mergedPath = pathJoin(
      result.directory ?? pathDirname(chunkPaths[0]),
      'upload-merged-trace.zip'
    );
    mergeTraceArchives(chunkPaths, mergedPath);

    const { sizeBytes } = await putFileToPresignedUrl(mergedPath, opts.upload.url);

    this.log('info', 'Uploaded merged trace archive to S3', {
      key: opts.upload.key,
      sizeBytes,
      chunkCount: chunkPaths.length,
    });

    return {
      ...result,
      s3Key: opts.upload.key,
      sizeBytes,
    };
  }

  private async execSaveVideo(
    opts: { upload?: ArtifactUploadTarget } | undefined
  ): Promise<Awaited<ReturnType<IBrowserClient['saveVideo']>>> {
    const client = this.playwrightClient as IBrowserClient;
    const result = await client.saveVideo(opts);
    if (!result || !result.videoPath) return result;
    if (!opts?.upload) return result;

    const { sizeBytes } = await putFileToPresignedUrl(result.videoPath, opts.upload.url);

    this.log('info', 'Uploaded video artifact to S3', {
      key: opts.upload.key,
      sizeBytes,
    });

    return {
      ...result,
      s3Key: opts.upload.key,
      sizeBytes,
      durationMs: result.screencastTelemetry?.encoderDurationMs ?? result.durationMs,
    };
  }
}
